// src/pages/ClientsPage.jsx
// Phase 1 only - real current data, no fabricated fields. Deliberately excludes:
// - a site/location column: company_data.json's `sites` array is empty and every
//   employee's `site` field is literally "Unassigned" - confirmed directly, not a
//   guess. Clients have no address/location field in their schema at all.
// - a client-type filter: both real clients default to the same "Business" type
//   (clientService.js hardcodes/defaults it), not meaningful to filter on yet.
// - contact_person, unless one is actually on file - defaults to "Unknown" and both
//   real clients still have that default, so showing it would look like real data
//   when it's just an unfilled placeholder.
// Revenue/margin/billing-rate/staffing come from businessService.getFinancialAnalysis()
// .clientBreakdowns (same source the Financial Reporting page and AIM's Finance/
// Scheduling domains already use, including the real covering-employee names and
// weekly-hours coverage added to that same function for this page - no second
// per-client data-fetching path). Status/type/contract terms come straight from the
// raw client record, already included in GET /api/business-data.
import { useState, useEffect } from 'react';
import { Users, DollarSign, AlertTriangle } from 'lucide-react';
import apiService from '../services/apiService';
import { formatCurrency, formatDate } from '../utils/helpers';
import { theme, rgba } from '../theme';

const STAFFING_META = {
  fully_staffed: { bg: 'rgba(34,197,94,0.18)', color: '#22c55e', label: 'Fully staffed' },
  partial:       { bg: 'rgba(245,158,11,0.18)', color: '#f59e0b', label: 'Partial staffing' },
  unassigned:    { bg: 'rgba(239,68,68,0.18)',  color: '#ef4444', label: 'Unassigned' },
};

function SummaryTile({ icon: Icon, label, value, accentRgb }) {
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      padding: '20px', borderRadius: '12px',
      background: rgba(theme.colors.tileBackgroundRgb, 0.8),
      border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
    }}>
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.5,
        background: `linear-gradient(135deg, rgba(${accentRgb},0.15), rgba(${accentRgb},0.02))`,
      }} />
      <div style={{ position: 'relative' }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '8px',
          background: `rgba(${accentRgb},0.18)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '10px',
        }}>
          <Icon size={16} color={`rgb(${accentRgb})`} />
        </div>
        <div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}
        </div>
        <div style={{ color: '#e2e8f0', fontSize: '24px', fontWeight: '700', marginTop: '2px' }}>{value}</div>
      </div>
    </div>
  );
}

function ClientCard({ client, breakdown }) {
  const staffing = STAFFING_META[breakdown?.staffingStatus] || STAFFING_META.unassigned;
  const covering = breakdown?.coveringEmployees || [];
  const needed = breakdown?.neededHoursPerWeek || 0;
  const assigned = breakdown?.assignedHoursPerWeek || 0;

  return (
    <div style={{
      background: rgba(theme.colors.tileBackgroundRgb, 0.8),
      border: '1px solid rgba(59,130,246,0.15)',
      borderLeft: `3px solid ${staffing.color}`,
      borderRadius: '10px',
      padding: '18px 22px',
      marginBottom: '16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>{client.name}</span>
          <span style={{
            padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
            background: client.status === 'Active' ? 'rgba(34,197,94,0.18)' : 'rgba(148,163,184,0.18)',
            color: client.status === 'Active' ? '#22c55e' : '#94a3b8',
          }}>
            {client.status || 'Unknown'}
          </span>
          {client.type && (
            <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: 'rgba(148,163,184,0.15)', color: '#94a3b8' }}>
              {client.type}
            </span>
          )}
        </div>
        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: staffing.bg, color: staffing.color }}>
          {staffing.label}
        </span>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '14px',
        padding: '12px 0', borderTop: '1px solid rgba(59,130,246,0.08)', borderBottom: '1px solid rgba(59,130,246,0.08)',
        marginBottom: '12px',
      }}>
        <div>
          <div style={{ color: '#64748b', fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Revenue</div>
          <div style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', marginTop: '2px' }}>{formatCurrency(breakdown?.revenue ?? client.monthly_revenue)}/mo</div>
        </div>
        <div>
          <div style={{ color: '#64748b', fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Margin</div>
          <div style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', marginTop: '2px' }}>
            {breakdown?.marginPercent != null ? `${breakdown.marginPercent.toFixed(1)}%` : '—'}
          </div>
        </div>
        <div>
          <div style={{ color: '#64748b', fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Billing Rate</div>
          <div style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', marginTop: '2px' }}>
            {breakdown?.billingRate ? `${formatCurrency(breakdown.billingRate)}/hr` : '—'}
          </div>
        </div>
        <div>
          <div style={{ color: '#64748b', fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Contract Start</div>
          <div style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', marginTop: '2px' }}>{formatDate(client.contract_start)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', fontSize: '13px', marginBottom: '6px' }}>
        <span style={{ color: '#64748b', minWidth: '88px', flexShrink: 0 }}>Staffed by</span>
        <span style={{ color: '#cbd5e1' }}>
          {covering.length > 0
            ? covering.map(c => `${c.name} (${c.hoursPerWeek}h/wk)`).join(', ')
            : 'No one currently assigned'}
        </span>
      </div>

      {needed > 0 && (
        <div style={{ display: 'flex', gap: '8px', fontSize: '13px', marginBottom: '6px' }}>
          <span style={{ color: '#64748b', minWidth: '88px', flexShrink: 0 }}>Coverage</span>
          <span style={{ color: assigned < needed ? '#f59e0b' : '#cbd5e1', fontWeight: assigned < needed ? '600' : '400' }}>
            {assigned} of {needed} hrs/week covered{assigned < needed ? ` - ${needed - assigned} hrs/week short` : ' - fully staffed'}
          </span>
        </div>
      )}

      {client.contractDescription && (
        <div style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
          <span style={{ color: '#64748b', minWidth: '88px', flexShrink: 0 }}>Contract terms</span>
          <span style={{ color: '#cbd5e1' }}>{client.contractDescription}</span>
        </div>
      )}

      {client.contact_person && client.contact_person !== 'Unknown' && (
        <div style={{ display: 'flex', gap: '8px', fontSize: '13px', marginTop: '6px' }}>
          <span style={{ color: '#64748b', minWidth: '88px', flexShrink: 0 }}>Contact</span>
          <span style={{ color: '#cbd5e1' }}>{client.contact_person}</span>
        </div>
      )}
    </div>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.getBusinessData()
      .then(data => {
        if (data?.success) {
          setClients(data.data?.clients || []);
          setBreakdowns(data.data?.financialAnalysis?.clientBreakdowns || []);
        } else {
          setError('Failed to load client data.');
        }
      })
      .catch(err => setError(err.message || 'Failed to load client data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '48px', color: '#94a3b8' }}>Loading...</div>;
  if (error) return <div style={{ padding: '48px', color: '#ef4444' }}>{error}</div>;

  const totalRevenue = breakdowns.reduce((sum, b) => sum + (b.revenue || 0), 0);
  const needingAttention = breakdowns.filter(b => b.staffingStatus !== 'fully_staffed').length;

  return (
    <div style={{ padding: '40px 48px', minHeight: '100vh' }}>
      <h1 style={{ color: '#e2e8f0', fontSize: '26px', margin: '0 0 6px' }}>Clients</h1>
      <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 32px' }}>
        Real current client data - revenue, staffing, and contract terms on file.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <SummaryTile icon={Users} label="Total Clients" value={clients.length} accentRgb="59,130,246" />
        <SummaryTile icon={DollarSign} label="Total Client Revenue" value={`${formatCurrency(totalRevenue)}/mo`} accentRgb="34,197,94" />
        <SummaryTile icon={AlertTriangle} label="Need Staffing Attention" value={`${needingAttention} of ${clients.length}`} accentRgb="245,158,11" />
      </div>

      <h2 style={{ color: '#e2e8f0', fontSize: '18px', margin: '0 0 16px' }}>All clients</h2>
      {clients.length === 0 ? (
        <div style={{
          padding: '32px', textAlign: 'center', color: '#64748b',
          background: rgba(theme.colors.tileBackgroundRgb, 0.8),
          border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
          borderRadius: '12px',
        }}>
          No clients on file yet.
        </div>
      ) : (
        clients.map(client => (
          <ClientCard
            key={client.id}
            client={client}
            breakdown={breakdowns.find(b => b.clientName === client.name)}
          />
        ))
      )}
    </div>
  );
}
